import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import WorkoutBuilderModal from '../WorkoutBuilderModal/WorkoutBuilderModal';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import { IActiveInjurySummary } from '../../../models/injury';
import { InjuryBadge } from '../../../shared/InjuryBadge/InjuryBadge';
import dayjs from 'dayjs';
import { formatFullName } from '../../../utils/functions';
import { Spinner } from "@chakra-ui/react";
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  X,
  Users,
  User,
  CalendarDays,
  Send,
  Plus,
  Check,
  Layers,
  Calendar,
  Repeat,
  ToggleLeft,
  ToggleRight,
  Dumbbell,
  Search,
  ChevronRight,
} from 'lucide-react';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomDatePicker from '../../../shared/CustomDatePicker/CustomDatePicker';
import DatePicker from 'react-datepicker';
import MultiDateInlinePicker from '../../../shared/MultiDateInlinePicker/MultiDateInlinePicker';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IWorkout, IAssignee, ICustomLoad } from '../../../models/programming';
import { WORKOUT_CATEGORY } from '../../../enums/programming';
import {
  WORKOUT_TYPE,
  ASSIGNMENT_TYPE,
  DAY_MAPPING,
  ASSIGNMENT_ORIGIN,
  PERIODICITY,
  LOAD_UNIT,
} from '../../../enums/programming';
import { addBaseUrl } from '../../../lib/DurationPicker/utils';
import { ITeam } from '../../../models/Team';
import { IUser2 } from '../../../models/user copy';
import { Paginated } from '../../../typings/Paginated';
import styles from './AssignWorkoutModal.module.scss';

interface AssignWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  workout: IWorkout | null;
  preSelectedDate?: Date | null;
  preSelectedAthleteIds?: string[];
  initialCategory?: WORKOUT_CATEGORY;
}

const WORKOUT_TYPE_LABELS: Record<WORKOUT_TYPE, string> = {
  [WORKOUT_TYPE.SINGLE_DAY]: 'Giorno singolo',
  [WORKOUT_TYPE.MULTI_DAY]: 'Multi-giorno',
};

const DAY_MAPPING_LABELS: Record<DAY_MAPPING, string> = {
  [DAY_MAPPING.CONSECUTIVE]: 'Giorni consecutivi',
  [DAY_MAPPING.SPECIFIC_DAYS]: 'Giorni specifici',
  [DAY_MAPPING.CUSTOM]: 'Personalizzato',
};

const PERIODICITY_LABELS: Record<string, string> = {
  none: 'Nessuna',
  [PERIODICITY.WEEKLY]: 'Settimanale',
  [PERIODICITY.BIWEEKLY]: 'Bisettimanale',
  [PERIODICITY.MONTHLY]: 'Mensile',
  [PERIODICITY.CUSTOM]: 'Custom',
};

type PeriodicityEndType = 'never' | 'after_n' | 'on_date';

const PERIODICITY_END_LABELS: Record<PeriodicityEndType, string> = {
  never: 'Mai',
  after_n: 'Dopo N volte',
  on_date: 'Il (data)',
};

interface AthleteLoadOverride {
  athleteId: string;
  exerciseId: string;
  oneRm: number | '';
  overrideValue: number | '';
  unit: LOAD_UNIT;
}

const AssignWorkoutModal: FC<AssignWorkoutModalProps> = ({ isOpen, onClose, workout: workoutProp, preSelectedDate, preSelectedAthleteIds, initialCategory }) => {
  const queryClient = useQueryClient();
  const { getTeams, getUsers, postProgrammingAssignment, getProgrammingWorkouts } = useEndpoints();
  const injuryEndpoints = useInjuryEndpoints();

  // When workout prop is null, allow picking from the library
  const [pickedWorkout, setPickedWorkout] = useState<IWorkout | null>(null);
  const [workoutPickerSearch, setWorkoutPickerSearch] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);

  const workout = workoutProp ?? pickedWorkout;
  const showWorkoutPicker = workoutProp === null && pickedWorkout === null;

  const workoutPickerQuery = useQuery({
    queryKey: ['programming-workouts-picker', workoutPickerSearch],
    queryFn: async () => {
      const { data } = await getProgrammingWorkouts({
        search: workoutPickerSearch || undefined,
        isTemplate: true,
        limit: 30,
      });
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: isOpen && workoutProp === null,
  });

  const pickerWorkouts: IWorkout[] = useMemo(
    () => (workoutPickerQuery.data as Paginated<IWorkout> | undefined)?.docs ?? (workoutPickerQuery.data as unknown as IWorkout[] | undefined) ?? [],
    [workoutPickerQuery.data]
  );

  // Reset picker when modal closes/reopens
  useEffect(() => {
    if (!isOpen) {
      setPickedWorkout(null);
      setWorkoutPickerSearch('');
    }
  }, [isOpen]);

  const [assignmentType, setAssignmentType] = useState<ASSIGNMENT_TYPE>(ASSIGNMENT_TYPE.SCHEDULED);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<string[]>([]);
  const [scheduledDates, setScheduledDates] = useState<string[]>([]);
  const [playlistStartDate, setPlaylistStartDate] = useState<string>('');

  // Pre-fill first date slot when a date is provided on open
  useEffect(() => {
    if (isOpen && preSelectedDate) {
      const formatted = dayjs(preSelectedDate).format('YYYY-MM-DD');
      setScheduledDates([formatted]);
    }
  }, [isOpen, preSelectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-select athletes when provided (e.g. from athlete profile page)
  useEffect(() => {
    if (isOpen && preSelectedAthleteIds && preSelectedAthleteIds.length > 0) {
      setSelectedAthleteIds(preSelectedAthleteIds);
    }
  }, [isOpen, preSelectedAthleteIds]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dayMapping, setDayMapping] = useState<DAY_MAPPING>(DAY_MAPPING.CONSECUTIVE);
  const [teamSearch, setTeamSearch] = useState('');
  const [athleteSearch, setAthleteSearch] = useState('');

  // Periodicity state
  const [periodicity, setPeriodicity] = useState<PERIODICITY | 'none'>('none');
  const [periodicityEndType, setPeriodicityEndType] = useState<PeriodicityEndType>('never');
  const [periodicityEndAfter, setPeriodicityEndAfter] = useState<number>(4);
  const [periodicityEndDate, setPeriodicityEndDate] = useState<string>('');

  // Personalizzazione carichi state
  const [customizeLoads, setCustomizeLoads] = useState(false);
  const [loadOverrides, setLoadOverrides] = useState<AthleteLoadOverride[]>([]);

  const teamsQuery = useQuery({
    queryKey: ['teams-for-assignment'],
    queryFn: async () => {
      const { data } = await getTeams({ limit: 100 });
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const athletesQuery = useQuery({
    queryKey: ['athletes-for-assignment'],
    queryFn: async () => {
      const { data } = await getUsers({ limit: 100, type: 'athlete' });
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const teams: ITeam[] = useMemo(
    () => (teamsQuery.data as Paginated<ITeam> | undefined)?.docs ?? (teamsQuery.data as ITeam[] | undefined) ?? [],
    [teamsQuery.data]
  );

  const athletes: IUser2[] = useMemo(
    () => (athletesQuery.data as Paginated<IUser2> | undefined)?.docs ?? (athletesQuery.data as IUser2[] | undefined) ?? [],
    [athletesQuery.data]
  );

  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    const lower = teamSearch.toLowerCase();
    return teams.filter((t) => t.name.toLowerCase().includes(lower));
  }, [teams, teamSearch]);

  const filteredAthletes = useMemo(() => {
    if (!athleteSearch.trim()) return athletes;
    const lower = athleteSearch.toLowerCase();
    return athletes.filter((a) =>
      `${a.firstName} ${a.lastName}`.toLowerCase().includes(lower)
    );
  }, [athletes, athleteSearch]);

  // Injuries for the first selected team (if any)
  const firstSelectedTeamId = selectedTeamIds[0] ?? null;
  const teamInjuriesQuery = useQuery<IActiveInjurySummary[]>({
    queryKey: ['team-injuries', firstSelectedTeamId],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getTeamInjuries(firstSelectedTeamId!);
      return data;
    },
    enabled: !!firstSelectedTeamId && isOpen,
    staleTime: 1000 * 60 * 5,
  });

  const injuredAthleteMap = useMemo(() => {
    const map: Record<string, IActiveInjurySummary> = {};
    (teamInjuriesQuery.data ?? []).forEach((inj) => { map[inj.athleteId] = inj; });
    return map;
  }, [teamInjuriesQuery.data]);

  const injuredSelectedCount = useMemo(
    () => selectedAthleteIds.filter((id) => !!injuredAthleteMap[id]).length,
    [selectedAthleteIds, injuredAthleteMap]
  );

  const injuredInListCount = useMemo(
    () => filteredAthletes.filter((a) => !!injuredAthleteMap[a._id] && !selectedAthleteIds.includes(a._id)).length,
    [filteredAthletes, injuredAthleteMap, selectedAthleteIds]
  );

  const assignMutation = useMutation({
    mutationFn: (body: Parameters<typeof postProgrammingAssignment>[0]) =>
      postProgrammingAssignment(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programming-assignments'] });
      handleClose();
    },
  });

  const handleClose = useCallback(() => {
    setAssignmentType(ASSIGNMENT_TYPE.SCHEDULED);
    setSelectedTeamIds([]);
    setSelectedAthleteIds([]);
    setScheduledDates([]);
    setPlaylistStartDate('');
    setDayMapping(DAY_MAPPING.CONSECUTIVE);
    setTeamSearch('');
    setAthleteSearch('');
    setPeriodicity('none');
    setPeriodicityEndType('never');
    setPeriodicityEndAfter(4);
    setPeriodicityEndDate('');
    setCustomizeLoads(false);
    setLoadOverrides([]);
    setPickedWorkout(null);
    setWorkoutPickerSearch('');
    onClose();
  }, [onClose]);

  const toggleTeam = useCallback((id: string) => {
    setSelectedTeamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleAthlete = useCallback((id: string) => {
    setSelectedAthleteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  // a48bb8: "Seleziona tutti" gli atleti filtrati (squadre da 20-30 atleti) con un click.
  const toggleSelectAllAthletes = useCallback(() => {
    setSelectedAthleteIds((prev) => {
      const filteredIds = filteredAthletes.map((a) => a._id);
      if (filteredIds.length === 0) return prev;
      const allSelected = filteredIds.every((id) => prev.includes(id));
      return allSelected
        ? prev.filter((id) => !filteredIds.includes(id))
        : Array.from(new Set([...prev, ...filteredIds]));
    });
  }, [filteredAthletes]);

  const removeTeam = useCallback((id: string) => {
    setSelectedTeamIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const removeAthlete = useCallback((id: string) => {
    setSelectedAthleteIds((prev) => prev.filter((x) => x !== id));
  }, []);

  // Extract all exercises from workout for load customization
  const allExercises = useMemo(() => {
    if (!workout) return [];
    const exercises: { exerciseId: string; name: string }[] = [];
    workout.days.forEach((day) => {
      day.blocks.forEach((block) => {
        (block as any).exercises?.forEach((ex: any) => {
          const id = typeof ex.exerciseId === 'string' ? ex.exerciseId : ex.exerciseId?._id;
          const rawName = typeof ex.exerciseId === 'string' ? ex.exerciseId : ex.exerciseId?.name;
          const name = typeof rawName === 'object' ? (rawName?.it || rawName?.en || id) : (rawName ?? id);
          if (id && !exercises.find((e) => e.exerciseId === id)) {
            exercises.push({ exerciseId: id, name });
          }
        });
      });
    });
    return exercises;
  }, [workout]);

  const updateLoadOverride = useCallback(
    (athleteId: string, exerciseId: string, field: 'oneRm' | 'overrideValue' | 'unit', value: number | '' | LOAD_UNIT) => {
      setLoadOverrides((prev) => {
        const idx = prev.findIndex((o) => o.athleteId === athleteId && o.exerciseId === exerciseId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], [field]: value };
          return updated;
        }
        const newEntry: AthleteLoadOverride = {
          athleteId,
          exerciseId,
          oneRm: '',
          overrideValue: '',
          unit: LOAD_UNIT.KG,
          [field]: value,
        };
        return [...prev, newEntry];
      });
    },
    []
  );

  const getLoadOverride = useCallback(
    (athleteId: string, exerciseId: string): AthleteLoadOverride | undefined =>
      loadOverrides.find((o) => o.athleteId === athleteId && o.exerciseId === exerciseId),
    [loadOverrides]
  );

  const handleSubmit = useCallback(() => {
    if (!workout) return;

    const assignees: IAssignee[] = [
      ...selectedTeamIds.map((id) => ({ type: 'team' as const, id })),
      ...selectedAthleteIds.map((id) => ({ type: 'athlete' as const, id })),
    ];

    if (assignees.length === 0) return;

    const validDates = scheduledDates.filter(Boolean);

    // Build periodicity fields
    const periodicityFields: Record<string, unknown> = {};
    if (isScheduled && periodicity !== 'none') {
      periodicityFields.periodicity = periodicity;
      periodicityFields.periodicityEndType = periodicityEndType;
      if (periodicityEndType === 'after_n') {
        periodicityFields.periodicityEndAfter = periodicityEndAfter;
      } else if (periodicityEndType === 'on_date' && periodicityEndDate) {
        periodicityFields.periodicityEndDate = periodicityEndDate;
      }
    }

    // Build custom loads from overrides
    const customLoads: ICustomLoad[] = [];
    if (customizeLoads && loadOverrides.length > 0) {
      loadOverrides.forEach((override) => {
        if (override.overrideValue !== '' && override.overrideValue > 0) {
          customLoads.push({
            athleteId: override.athleteId,
            exerciseId: override.exerciseId,
            loads: [{ value: Number(override.overrideValue), unit: override.unit }],
          });
        }
      });
    }

    const body: Parameters<typeof postProgrammingAssignment>[0] = {
      workoutId: workout._id,
      assignmentType,
      assignees,
      createdFrom: ASSIGNMENT_ORIGIN.LIBRARY,
      ...(assignmentType === ASSIGNMENT_TYPE.SCHEDULED && validDates.length > 0
        ? { scheduledDates: validDates }
        : {}),
      ...(assignmentType === ASSIGNMENT_TYPE.SCHEDULED &&
      (workout as IWorkout).type === WORKOUT_TYPE.MULTI_DAY
        ? { dayMapping }
        : {}),
      ...(assignmentType === ASSIGNMENT_TYPE.PLAYLIST && playlistStartDate
        ? { startDate: playlistStartDate }
        : {}),
      ...periodicityFields,
      ...(customLoads.length > 0 ? { customLoads } : {}),
    };

    assignMutation.mutate(body);
  }, [
    workout,
    assignmentType,
    selectedTeamIds,
    selectedAthleteIds,
    scheduledDates,
    playlistStartDate,
    dayMapping,
    assignMutation,
    periodicity,
    periodicityEndType,
    periodicityEndAfter,
    periodicityEndDate,
    customizeLoads,
    loadOverrides,
  ]);

  const validScheduledDates = scheduledDates.filter(Boolean);
  const isSubmitDisabled =
    assignMutation.isPending ||
    (selectedTeamIds.length === 0 && selectedAthleteIds.length === 0) ||
    (assignmentType === ASSIGNMENT_TYPE.SCHEDULED && validScheduledDates.length === 0);

  const selectedTeamsData = useMemo(
    () => teams.filter((t) => selectedTeamIds.includes(t._id)),
    [teams, selectedTeamIds]
  );

  const selectedAthletesData = useMemo(
    () => athletes.filter((a) => selectedAthleteIds.includes(a._id)),
    [athletes, selectedAthleteIds]
  );

  if (!workout && !showWorkoutPicker) return null;

  // Workout picker step — shown when no workout prop and none picked yet
  if (showWorkoutPicker) {
    return (
      <>
      {builderOpen && (
        <WorkoutBuilderModal
          isOpen={builderOpen}
          onClose={() => setBuilderOpen(false)}
          workout={null}
          onSaved={(newWorkout) => {
            setPickedWorkout(newWorkout);
            setBuilderOpen(false);
          }}
          initialCategory={initialCategory}
        />
      )}

      <GenericModalV2
        isOpen={isOpen}
        onClose={handleClose}
        size="lg"
        title="Seleziona scheda da assegnare"
        Footer={
          <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'space-between' }}>
            <CustomButton color="outline" onClick={() => setBuilderOpen(true)}>
              <Plus size={14} strokeWidth={2.5} />
              Crea nuova scheda
            </CustomButton>
            <CustomButton color="outline" onClick={handleClose}>
              Annulla
            </CustomButton>
          </div>
        }
      >
        <div className={styles.content}>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <Search
              size={14}
              strokeWidth={2}
              style={{
                position: 'absolute', left: 10, top: '50%',
                transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder="Cerca scheda..."
              value={workoutPickerSearch}
              onChange={(e) => setWorkoutPickerSearch(e.target.value)}
              style={{
                width: '100%', paddingLeft: 32, paddingRight: 12,
                paddingTop: 8, paddingBottom: 8,
                border: '1px solid #E2E8F0', borderRadius: 8,
                fontSize: 14, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          {workoutPickerQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <LeopartLoader size="sm" />
            </div>
          ) : pickerWorkouts.length === 0 ? (
            <p style={{ color: '#A0AEC0', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
              Nessuna scheda trovata.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 400, overflowY: 'auto' }}>
              {pickerWorkouts.map((w) => (
                <button
                  key={w._id}
                  onClick={() => setPickedWorkout(w)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 8, border: '1px solid #E2E8F0',
                    background: 'var(--c-card-bg)', cursor: 'pointer', textAlign: 'left',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F7FAFC'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#2D3748' }}>{w.title}</span>
                    <span style={{ fontSize: 12, color: '#718096' }}>
                      {w.type === WORKOUT_TYPE.MULTI_DAY ? 'Multi-giorno' : 'Giorno singolo'}
                      {' · '}{w.days.length} giorn{w.days.length === 1 ? 'o' : 'i'}
                    </span>
                  </div>
                  <ChevronRight size={16} strokeWidth={2} style={{ color: '#A0AEC0', flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}
        </div>
      </GenericModalV2>
      </>
    );
  }

  // At this point workout is guaranteed non-null (either from prop or picker)
  const activeWorkout = workout as IWorkout;
  const isMultiDay = activeWorkout.type === WORKOUT_TYPE.MULTI_DAY;
  const isScheduled = assignmentType === ASSIGNMENT_TYPE.SCHEDULED;
  const totalSelected = selectedTeamIds.length + selectedAthleteIds.length;

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title="Assegna scheda"
      Footer={
        <>
          <CustomButton color="outline" onClick={handleClose} disabled={assignMutation.isPending}>
            Annulla
          </CustomButton>
          <CustomButton
            color="secondary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            isLoading={assignMutation.isPending}
          >
            <Send size={14} strokeWidth={2.5} />
            Assegna
          </CustomButton>
        </>
      }
    >
      <div className={styles.content}>
        {/* Workout info */}
        <div className={styles.workoutInfo}>
          <p className={styles.workoutTitle}>{activeWorkout.title}</p>
          <div className={styles.workoutMeta}>
            <span className={styles.typeBadge}>
              {isMultiDay
                ? <Layers size={11} strokeWidth={2.5} />
                : <CalendarDays size={11} strokeWidth={2.5} />
              }
              {WORKOUT_TYPE_LABELS[activeWorkout.type]}
            </span>
            <span className={styles.metaItem}>
              {activeWorkout.days.length} giorn{activeWorkout.days.length === 1 ? 'o' : 'i'}
            </span>
          </div>
        </div>

        {/* Assignment type */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Tipo di assegnazione</p>
          <div className={styles.radioGroup}>
            <label
              className={`${styles.radioOption} ${assignmentType === ASSIGNMENT_TYPE.SCHEDULED ? styles.radioOptionActive : ''}`}
            >
              <input
                type="radio"
                name="assignmentType"
                value={ASSIGNMENT_TYPE.SCHEDULED}
                checked={assignmentType === ASSIGNMENT_TYPE.SCHEDULED}
                onChange={() => setAssignmentType(ASSIGNMENT_TYPE.SCHEDULED)}
                className={styles.hiddenInput}
              />
              <div className={`${styles.radioCircle} ${assignmentType === ASSIGNMENT_TYPE.SCHEDULED ? styles.radioCircleChecked : ''}`}>
                {assignmentType === ASSIGNMENT_TYPE.SCHEDULED && <div className={styles.radioInner} />}
              </div>
              <div className={styles.radioLabel}>
                <span className={styles.radioTitle}>
                  <Calendar size={14} strokeWidth={2} />
                  Schedulata
                </span>
                <span className={styles.radioDesc}>Assegna con date specifiche nel calendario</span>
              </div>
            </label>
            <label
              className={`${styles.radioOption} ${assignmentType === ASSIGNMENT_TYPE.PLAYLIST ? styles.radioOptionActive : ''}`}
            >
              <input
                type="radio"
                name="assignmentType"
                value={ASSIGNMENT_TYPE.PLAYLIST}
                checked={assignmentType === ASSIGNMENT_TYPE.PLAYLIST}
                onChange={() => setAssignmentType(ASSIGNMENT_TYPE.PLAYLIST)}
                className={styles.hiddenInput}
              />
              <div className={`${styles.radioCircle} ${assignmentType === ASSIGNMENT_TYPE.PLAYLIST ? styles.radioCircleChecked : ''}`}>
                {assignmentType === ASSIGNMENT_TYPE.PLAYLIST && <div className={styles.radioInner} />}
              </div>
              <div className={styles.radioLabel}>
                <span className={styles.radioTitle}>
                  <Layers size={14} strokeWidth={2} />
                  Playlist
                </span>
                <span className={styles.radioDesc}>L'atleta esegue quando vuole, senza date</span>
              </div>
            </label>
          </div>
        </div>

        {/* Assignees */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>
            Destinatari
            {totalSelected > 0 && (
              <span className={styles.sectionCount}>{totalSelected}</span>
            )}
          </p>
          {injuredSelectedCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff5f5', borderRadius: 8, border: '1px solid #fecaca', marginBottom: 8, fontFamily: 'Satoshi, sans-serif', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
              ⚠ {injuredSelectedCount} atleta{injuredSelectedCount > 1 ? 'i' : ''} selezionato{injuredSelectedCount > 1 ? 'i' : ''} con infortunio attivo — verificare prima di assegnare
            </div>
          )}

          {/* Selected chips */}
          {(selectedTeamsData.length > 0 || selectedAthletesData.length > 0) && (
            <div className={styles.chipsRow}>
              {selectedTeamsData.map((team) => (
                <span key={team._id} className={styles.chip}>
                  <Users size={12} strokeWidth={2.5} />
                  {team.name}
                  <button className={styles.chipRemove} onClick={() => removeTeam(team._id)}>
                    <X size={10} strokeWidth={3} />
                  </button>
                </span>
              ))}
              {selectedAthletesData.map((athlete) => (
                <span key={athlete._id} className={`${styles.chip} ${styles.chipAthlete}`}>
                  <User size={12} strokeWidth={2.5} />
                  {formatFullName(athlete.firstName, athlete.lastName)}
                  <button className={styles.chipRemove} onClick={() => removeAthlete(athlete._id)}>
                    <X size={10} strokeWidth={3} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className={styles.assigneesGrid}>
            {/* Teams */}
            <div className={styles.assigneePanel}>
              <div className={styles.panelHeader}>
                <Users size={14} strokeWidth={2.5} />
                <span>Team</span>
                {selectedTeamIds.length > 0 && (
                  <span className={styles.panelCount}>{selectedTeamIds.length}</span>
                )}
              </div>
              <input
                type="text"
                placeholder="Cerca team..."
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                className={styles.panelSearch}
              />
              <div className={styles.listBox}>
                {teamsQuery.isLoading ? (
                  <div className={styles.listLoading}><Spinner size="sm" /></div>
                ) : filteredTeams.length === 0 ? (
                  <p className={styles.listEmpty}>Nessun team trovato</p>
                ) : (
                  filteredTeams.map((team) => {
                    const isSelected = selectedTeamIds.includes(team._id);
                    const teamImg = (team as any).image ? addBaseUrl(1, (team as any).image) : undefined;
                    const initials = team.name?.slice(0, 2).toUpperCase() ?? '??';
                    return (
                      <label key={team._id} className={`${styles.checkRow} ${isSelected ? styles.checkRowActive : ''}`}>
                        <div className={`${styles.checkSquare} ${isSelected ? styles.checkSquareChecked : ''}`}>
                          {isSelected && <Check size={12} strokeWidth={3} />}
                        </div>
                        <div className={styles.checkAvatar}>
                          {teamImg ? (
                            <img src={teamImg} alt={team.name} className={styles.checkAvatarImg} />
                          ) : (
                            <span className={styles.checkAvatarFallback}>{initials}</span>
                          )}
                        </div>
                        <div className={styles.checkInfo}>
                          <span className={styles.checkLabel}>{team.name}</span>
                          <span className={styles.checkSub}>{(team as any).users?.length ?? 0} atleti</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTeam(team._id)}
                          className={styles.hiddenInput}
                        />
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Athletes */}
            <div className={styles.assigneePanel}>
              <div className={styles.panelHeader}>
                <User size={14} strokeWidth={2.5} />
                <span>Atleti</span>
                {selectedAthleteIds.length > 0 && (
                  <span className={styles.panelCount}>{selectedAthleteIds.length}</span>
                )}
                {/* a48bb8: seleziona/deseleziona tutti gli atleti filtrati con un click */}
                <button
                  type="button"
                  onClick={toggleSelectAllAthletes}
                  disabled={filteredAthletes.length === 0}
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#013344',
                    background: 'none',
                    border: 'none',
                    cursor: filteredAthletes.length === 0 ? 'default' : 'pointer',
                    fontFamily: 'Satoshi, sans-serif',
                    opacity: filteredAthletes.length === 0 ? 0.4 : 1,
                  }}
                >
                  {filteredAthletes.length > 0 &&
                  filteredAthletes.every((a) => selectedAthleteIds.includes(a._id))
                    ? 'Deseleziona tutti'
                    : 'Seleziona tutti'}
                </button>
              </div>
              <input
                type="text"
                placeholder="Cerca atleta..."
                value={athleteSearch}
                onChange={(e) => setAthleteSearch(e.target.value)}
                className={styles.panelSearch}
              />
              {injuredInListCount > 0 && !!firstSelectedTeamId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe', marginBottom: 8, fontFamily: 'Satoshi, sans-serif', fontSize: 12, color: '#1e40af', fontWeight: 600 }}>
                  🤕 {injuredInListCount} atleta{injuredInListCount > 1 ? 'i' : ''} con infortunio attivo esclus{injuredInListCount > 1 ? 'i' : 'o'} per default — selezionali manualmente per schede rehab
                </div>
              )}
              <div className={styles.listBox}>
                {athletesQuery.isLoading ? (
                  <div className={styles.listLoading}><Spinner size="sm" /></div>
                ) : filteredAthletes.length === 0 ? (
                  <p className={styles.listEmpty}>Nessun atleta trovato</p>
                ) : (
                  filteredAthletes.map((athlete) => {
                    const isSelected = selectedAthleteIds.includes(athlete._id);
                    const athleteImg = (athlete as any).image?.image ? addBaseUrl(1, (athlete as any).image.image) : undefined;
                    const athleteInitials = `${athlete.firstName?.[0] ?? ''}${athlete.lastName?.[0] ?? ''}`.toUpperCase();
                    return (
                      <label key={athlete._id} className={`${styles.checkRow} ${isSelected ? styles.checkRowActive : ''}`}>
                        <div className={`${styles.checkSquare} ${isSelected ? styles.checkSquareChecked : ''}`}>
                          {isSelected && <Check size={12} strokeWidth={3} />}
                        </div>
                        <div className={styles.checkAvatar}>
                          {athleteImg ? (
                            <img src={athleteImg} alt={formatFullName(athlete.firstName, athlete.lastName)} className={styles.checkAvatarImg} />
                          ) : (
                            <span className={styles.checkAvatarFallback}>{athleteInitials}</span>
                          )}
                        </div>
                        <div className={styles.checkInfo}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className={styles.checkLabel}>
                              {formatFullName(athlete.firstName, athlete.lastName)}
                            </span>
                            {injuredAthleteMap[athlete._id] && (
                              <InjuryBadge size="sm" injury={injuredAthleteMap[athlete._id]} />
                            )}
                          </div>
                          <span className={styles.checkSub}>{athlete.email}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleAthlete(athlete._id)}
                          className={styles.hiddenInput}
                        />
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scheduled section — calendario inline multi-day */}
        {isScheduled && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>
              <CalendarDays size={14} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
              Date allenamento
            </p>
            <MultiDateInlinePicker
              value={scheduledDates}
              onChange={setScheduledDates}
            />

            {/* Day mapping */}
            {isMultiDay && (
              <div className={styles.section} style={{ marginTop: 4 }}>
                <p className={styles.sectionLabel}>
                  <Layers size={14} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
                  Distribuzione giorni
                </p>
                <div className={styles.dayMappingGrid}>
                  {Object.values(DAY_MAPPING).map((v) => {
                    const isActive = dayMapping === v;
                    const icons: Record<DAY_MAPPING, React.ReactNode> = {
                      [DAY_MAPPING.CONSECUTIVE]: <CalendarDays size={16} strokeWidth={2} />,
                      [DAY_MAPPING.SPECIFIC_DAYS]: <Calendar size={16} strokeWidth={2} />,
                      [DAY_MAPPING.CUSTOM]: <Layers size={16} strokeWidth={2} />,
                    };
                    const descriptions: Record<DAY_MAPPING, string> = {
                      [DAY_MAPPING.CONSECUTIVE]: 'I giorni della scheda vengono distribuiti uno dopo l\'altro',
                      [DAY_MAPPING.SPECIFIC_DAYS]: 'Scegli in quali giorni della settimana assegnare',
                      [DAY_MAPPING.CUSTOM]: 'Definisci una distribuzione personalizzata',
                    };
                    return (
                      <label
                        key={v}
                        className={`${styles.dayMappingCard} ${isActive ? styles.dayMappingCardActive : ''}`}
                      >
                        <input
                          type="radio"
                          name="dayMapping"
                          value={v}
                          checked={isActive}
                          onChange={() => setDayMapping(v)}
                          className={styles.hiddenInput}
                        />
                        <div className={`${styles.dayMappingIcon} ${isActive ? styles.dayMappingIconActive : ''}`}>
                          {icons[v]}
                        </div>
                        <div className={styles.dayMappingText}>
                          <span className={styles.dayMappingTitle}>{DAY_MAPPING_LABELS[v]}</span>
                          <span className={styles.dayMappingDesc}>{descriptions[v]}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Playlist — data di inizio accesso */}
        {!isScheduled && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>
              <CalendarDays size={14} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
              Data di inizio
            </p>
            <div className={styles.playlistStartSection}>
              <p className={styles.playlistStartDesc}>
                Da quando l'atleta può accedere a questa playlist (opzionale)
              </p>
              <CustomDatePicker
                date={playlistStartDate ? new Date(playlistStartDate + 'T12:00:00') : null}
                onChange={(d) => setPlaylistStartDate(d ? dayjs(d).format('YYYY-MM-DD') : '')}
                placeholder="Seleziona data (opzionale)"
                showIcon={false}
                isClearable
              />
            </div>
          </div>
        )}

        {/* Periodicity / Ripetizione - solo per Schedulata */}
        {isScheduled && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>
              <Repeat size={14} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
              Ripetizione
            </p>
            <div className={styles.periodicityRow}>
              {Object.entries(PERIODICITY_LABELS).map(([key, label]) => {
                const isActive = periodicity === key;
                return (
                  <button
                    key={key}
                    className={`${styles.periodicityChip} ${isActive ? styles.periodicityChipActive : ''}`}
                    onClick={() => setPeriodicity(key as PERIODICITY | 'none')}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Condizioni fine periodicita - periodicityEnd */}
            {periodicity !== 'none' && (
              <div className={styles.periodicityEndSection}>
                <p className={styles.subsectionLabel}>Termina</p>
                <div className={styles.periodicityEndRow}>
                  {(Object.keys(PERIODICITY_END_LABELS) as PeriodicityEndType[]).map((endKey) => {
                    const isActive = periodicityEndType === endKey;
                    return (
                      <button
                        key={endKey}
                        className={`${styles.periodicityEndChip} ${isActive ? styles.periodicityEndChipActive : ''}`}
                        onClick={() => setPeriodicityEndType(endKey)}
                      >
                        {PERIODICITY_END_LABELS[endKey]}
                      </button>
                    );
                  })}
                </div>

                {periodicityEndType === 'after_n' && (
                  <div className={styles.periodicityEndInput}>
                    <label className={styles.inlineLabel}>Dopo</label>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={periodicityEndAfter}
                      onChange={(e) => setPeriodicityEndAfter(Math.max(1, parseInt(e.target.value) || 1))}
                      className={styles.smallNumberInput}
                    />
                    <label className={styles.inlineLabel}>volte</label>
                  </div>
                )}

                {periodicityEndType === 'on_date' && (
                  <div className={styles.periodicityEndInput}>
                    <label className={styles.inlineLabel}>Il</label>
                    <CustomDatePicker
                      date={periodicityEndDate ? new Date(periodicityEndDate) : null}
                      onChange={(d) => setPeriodicityEndDate(d ? d.toISOString().split('T')[0] : '')}
                      placeholder="Data fine"
                      showIcon={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Personalizzazione carichi - Playlist e Schedulata */}
        <div className={styles.section}>
          <div className={styles.toggleRow}>
            <button
              className={styles.toggleBtn}
              onClick={() => setCustomizeLoads((prev) => !prev)}
            >
              {customizeLoads
                ? <ToggleRight size={22} strokeWidth={2} className={styles.toggleIconActive} />
                : <ToggleLeft size={22} strokeWidth={2} className={styles.toggleIconInactive} />
              }
            </button>
            <div className={styles.toggleLabelGroup}>
              <span className={styles.sectionLabel} style={{ marginBottom: 0 }}>
                <Dumbbell size={14} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
                Personalizzazione carichi
              </span>
              <span className={styles.toggleDesc}>Override manuale dei carichi basato su 1RM per atleta</span>
            </div>
          </div>

          {customizeLoads && selectedAthleteIds.length > 0 && allExercises.length > 0 && (
            <div className={styles.loadCardsContainer}>
              {selectedAthletesData.map((athlete) => (
                <div key={athlete._id} className={styles.loadAthleteCard}>
                  <div className={styles.loadAthleteHeader}>
                    <div className={styles.loadAthleteAvatar}>
                      {athlete.firstName?.[0]}{athlete.lastName?.[0]}
                    </div>
                    <span className={styles.loadAthleteName}>
                      {formatFullName(athlete.firstName, athlete.lastName)}
                    </span>
                  </div>
                  <div className={styles.loadExerciseList}>
                    {allExercises.map((exercise) => {
                      const override = getLoadOverride(athlete._id, exercise.exerciseId);
                      return (
                        <div key={exercise.exerciseId} className={styles.loadExerciseRow}>
                          <span className={styles.loadExerciseName}>{exercise.name}</span>
                          <div className={styles.loadFieldsRow}>
                            <div className={styles.loadFieldGroup}>
                              <label className={styles.loadFieldLabel}>1RM</label>
                              <input
                                type="number"
                                min={0}
                                step={0.5}
                                placeholder="—"
                                value={override?.oneRm ?? ''}
                                onChange={(e) =>
                                  updateLoadOverride(
                                    athlete._id,
                                    exercise.exerciseId,
                                    'oneRm',
                                    e.target.value === '' ? '' : parseFloat(e.target.value)
                                  )
                                }
                                className={styles.loadInput}
                              />
                            </div>
                            <div className={styles.loadFieldGroup}>
                              <label className={styles.loadFieldLabel}>Override</label>
                              <input
                                type="number"
                                min={0}
                                step={0.5}
                                placeholder="—"
                                value={override?.overrideValue ?? ''}
                                onChange={(e) =>
                                  updateLoadOverride(
                                    athlete._id,
                                    exercise.exerciseId,
                                    'overrideValue',
                                    e.target.value === '' ? '' : parseFloat(e.target.value)
                                  )
                                }
                                className={styles.loadInput}
                              />
                            </div>
                            <div className={styles.loadFieldGroup}>
                              <label className={styles.loadFieldLabel}>Unità</label>
                              <select
                                value={override?.unit ?? LOAD_UNIT.KG}
                                onChange={(e) =>
                                  updateLoadOverride(
                                    athlete._id,
                                    exercise.exerciseId,
                                    'unit',
                                    e.target.value as LOAD_UNIT
                                  )
                                }
                                className={styles.loadSelect}
                              >
                                <option value={LOAD_UNIT.KG}>kg</option>
                                <option value={LOAD_UNIT.LB}>lb</option>
                                <option value={LOAD_UNIT.PERCENT_1RM}>%1RM</option>
                                <option value={LOAD_UNIT.PERCENT_BW}>%BW</option>
                                <option value={LOAD_UNIT.RPE}>RPE</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {customizeLoads && selectedAthleteIds.length === 0 && (
            <p className={styles.loadHint}>Seleziona almeno un atleta per personalizzare i carichi.</p>
          )}
        </div>

        {/* Validation hint */}
        {totalSelected === 0 && (
          <p className={styles.validationHint}>Seleziona almeno un team o un atleta per procedere.</p>
        )}
      </div>
    </GenericModalV2>
  );
};

export default AssignWorkoutModal;
